(() => {
  'use strict';
  if (window.FurtherRelatedItems) return;
  const caches = new WeakMap();
  const attempts = new WeakSet();
  const TTL = 300000;
  const MAX_CACHE = 20;
  const MAX_CANDIDATES = 14;

  function masterKey(value) {
    // The catalog updater reserves 9,900,000,000 + release ID for missing masters.
    if (!/^\d+$/.test(String(value || ''))) return '';
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 && id < 9900000000 ? String(id) : '';
  }

  function releaseKey(artists, title) {
    try {
      const names = JSON.parse(artists || 'null');
      if (!Array.isArray(names) || !names.length || !title?.trim()) return '';
      const normalize = value => String(value).normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
      return JSON.stringify([names.map(normalize).sort(), normalize(title)]);
    } catch { return ''; }
  }

  function nativeRequest(client, body) {
    if (typeof client.customPost === 'function') return client.customPost({ path: '1/indexes/*/recommendations', body });
    if (typeof client.transporter?.write === 'function') return client.transporter.write({ method: 'POST', path: '1/indexes/*/recommendations', data: body });
    throw new Error('client-unavailable');
  }

  function deadline(promise, milliseconds) {
    let timer;
    return Promise.race([promise, new Promise((resolve, reject) => { timer = setTimeout(() => reject(new Error('timeout')), milliseconds); })]).finally(() => clearTimeout(timer));
  }

  function recommendations(client, objectID) {
    let cache = caches.get(client);
    if (!cache) { cache = new Map(); caches.set(client, cache); }
    const entry = cache.get(objectID);
    if (entry && entry.expires > Date.now()) return entry.promise;
    cache.delete(objectID);
    while (cache.size >= MAX_CACHE) cache.delete(cache.keys().next().value);
    const promise = deadline(Promise.resolve().then(() => nativeRequest(client, {
      requests: [{
        indexName: 'shopify_products', model: 'related-products', objectID,
        threshold: 0, maxRecommendations: MAX_CANDIDATES,
        queryParameters: {
          filters: 'inventory_available:true', analytics: false, clickAnalytics: false,
          enablePersonalization: false,
          attributesToRetrieve: ['objectID', 'id', 'handle', 'inventory_available'],
          attributesToHighlight: [], attributesToSnippet: [],
        },
      }],
    })), 4000);
    const stored = { promise, expires: Date.now() + TTL };
    cache.set(objectID, stored);
    promise.catch(() => { stored.expires = Date.now() + 30000; });
    return promise;
  }

  function candidates(response, sourceProductID, sourceObjectID) {
    const hits = response?.results?.[0]?.hits;
    if (!Array.isArray(hits)) throw new Error('invalid-response');
    const seen = new Set([sourceProductID]);
    return hits.slice(0, MAX_CANDIDATES).filter(hit => {
      const id = String(hit.id || '');
      if (!/^\d+$/.test(id) || seen.has(id) || String(hit.objectID) === sourceObjectID) return false;
      if (hit.inventory_available !== true || typeof hit.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(hit.handle)) return false;
      seen.add(id);
      return true;
    });
  }

  async function card(hit, sectionID, routeRoot, signal) {
    const url = new URL(`${routeRoot}products/${encodeURIComponent(hit.handle)}`, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('invalid-route');
    url.searchParams.set('section_id', sectionID);
    const response = await fetch(url.pathname + url.search, { signal, cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('card-unavailable');
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = document.querySelector('template[data-further-related-card]');
    if (!template || template.dataset.productId !== String(hit.id) || template.dataset.available !== 'true' || template.dataset.inStock !== 'true') return null;
    const item = template.content.querySelector('.related-products__item');
    if (!item || item.dataset.productId !== String(hit.id)) return null;
    const clone = item.cloneNode(true);
    clone.querySelectorAll('script').forEach(script => script.remove());
    return clone;
  }

  async function load(element) {
    if (element.dataset.algoliaRelatedEnabled !== 'true' || element.classList.contains('complementary-products')) return false;
    if (attempts.has(element)) return false;
    attempts.add(element);
    const sourceProductID = element.dataset.sourceProductId;
    const sourceObjectID = element.dataset.sourceObjectId;
    const sourceSKU = element.dataset.sourceSku;
    const sourceArtists = element.dataset.sourceArtists;
    const sourceReleaseTitle = element.dataset.sourceReleaseTitle;
    const sourceMasterID = element.dataset.sourceMasterId;
    const sectionID = element.dataset.sectionId;
    const limit = Number(element.dataset.limit);
    const url = element.dataset.url;
    const routeRoot = window.Shopify?.routes?.root || '/';
    const layout = element.parentElement?.querySelector('template[data-further-related-layout]');
    const runtime = window.algoliaShopify;
    const client = runtime?.searchClient;
    if (!/^\d+$/.test(sourceProductID || '') || !/^\d+$/.test(sourceObjectID || '') || !sectionID || !Number.isInteger(limit) || limit < 2 || limit > 10 || !layout || runtime?.config?.app_id !== 'QBDNEPUQ59' || !client) {
      element.dataset.recommendationSource = 'shopify';
      element.dataset.algoliaRelatedStatus = 'configuration-unavailable';
      return false;
    }
    const current = () => element.isConnected && element.dataset.algoliaRelatedEnabled === 'true' && element.dataset.sourceProductId === sourceProductID && element.dataset.sourceObjectId === sourceObjectID && element.dataset.sourceSku === sourceSKU && element.dataset.sourceArtists === sourceArtists && element.dataset.sourceReleaseTitle === sourceReleaseTitle && element.dataset.sourceMasterId === sourceMasterID && element.dataset.sectionId === sectionID && Number(element.dataset.limit) === limit && element.dataset.url === url;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await recommendations(client, sourceObjectID);
      if (!current()) return false;
      const hits = candidates(response, sourceProductID, sourceObjectID);
      const minimum = Math.min(8, limit);
      if (hits.length < minimum) throw new Error('insufficient-results');
      const cards = new Array(hits.length);
      let cursor = 0;
      await Promise.all(Array.from({ length: Math.min(3, hits.length) }, async () => {
        while (cursor < hits.length && !controller.signal.aborted && current()) {
          const index = cursor++;
          try { cards[index] = await card(hits[index], sectionID, routeRoot, controller.signal); }
          catch { cards[index] = null; }
        }
      }));
      if (!current()) return false;
      const seenSKU = new Set(/^\d+$/.test(sourceSKU || '') ? [sourceSKU] : []);
      const sourceMaster = masterKey(sourceMasterID);
      const seenMasters = new Set(sourceMaster ? [sourceMaster] : []);
      const sourceRelease = releaseKey(sourceArtists, sourceReleaseTitle);
      const seenReleases = new Map(sourceRelease ? [[sourceRelease, sourceMaster]] : []);
      const valid = cards.filter(item => {
        if (!item) return false;
        const sku = item.dataset.sku;
        const master = masterKey(item.dataset.masterId);
        if (master && seenMasters.has(master)) return false;
        if (/^\d+$/.test(sku || '')) {
          if (seenSKU.has(sku)) return false;
        }
        const release = releaseKey(item.dataset.artists, item.dataset.releaseTitle);
        if (release) {
          if (seenReleases.has(release) && (!master || !seenReleases.get(release))) return false;
          seenReleases.set(release, master);
        }
        if (master) seenMasters.add(master);
        if (/^\d+$/.test(sku || '')) seenSKU.add(sku);
        return true;
      }).slice(0, limit);
      if (valid.length < minimum) throw new Error('insufficient-current-stock');
      const fragment = layout.content.cloneNode(true);
      const grid = fragment.querySelector('.product-grid');
      if (!grid) throw new Error('layout-unavailable');
      valid.forEach(item => grid.appendChild(item));
      element.replaceChildren(fragment);
      element.classList.add('product-recommendations--loaded');
      element.dataset.recommendationSource = 'algolia-related-products';
      element.dataset.algoliaRelatedStatus = 'ready';
      element.dispatchEvent(new CustomEvent('product-recommendations:updated', { bubbles: true, detail: { root: element } }));
      return true;
    } catch {
      if (current()) {
        element.dataset.recommendationSource = 'shopify';
        element.dataset.algoliaRelatedStatus = 'fallback';
      }
      return false;
    } finally {
      clearTimeout(timer);
      controller.abort();
    }
  }
  window.FurtherRelatedItems = Object.freeze({ load });
})();
